import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import './CRMConversationModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de conversa fullscreen para CRM Multi
 * Permite visualizar e enviar mensagens diretamente do card
 */
export default function CRMConversationModal({
  isOpen,
  onClose,
  contact,
  connectionId,
  companyId,
  card
}) {
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [error, setError] = useState(null);
  
  const messagesContainerRef = useRef(null);
  const channelRef = useRef(null);

  // Estados para mídia
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, mime, name }
  const [pendingDocument, setPendingDocument] = useState(null); // { dataUrl, mime, name, size }
  const [isRecording, setIsRecording] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxType, setLightboxType] = useState('');

  // Refs para mídia
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const emojiPickerRef = useRef(null);
  const inputMsgRef = useRef(null);

  // Lista de emojis
  const EMOJIS = [
    '😀','😁','😂','🤣','😊','😍','😘','😜','🤩','🤔','🤨','😐','🙄','😴','🤤','😢','😭','😡',
    '👍','👎','🙏','👏','🙌','💪','🔥','✨','💖','🎉','✅','❌','⚠️','⭐','🌟','🌞','🌙','☔','☕',
    '🍕','🍔','🍻','🎯','📞','📷'
  ];

  // Carregar conversa e mensagens quando abrir
  useEffect(() => {
    if (isOpen && contact?.id && connectionId) {
      loadConversation();
      loadConnectionInfo();
    }
    
    return () => {
      // Cleanup do canal realtime
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, contact?.id, connectionId]);

  // Scroll para última mensagem quando carregar
  useEffect(() => {
    console.log('[CRMConversationModal] Mensagens atualizadas:', messages.length, messages.map(m => ({ id: m.id, tipo: m.tipoConteudo, audio: m.audioUrl, image: m.imageUrl })));
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Configurar Realtime quando tiver conversa
  useEffect(() => {
    if (!conversation?.id) return;

    // Remover canal anterior
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Criar novo canal
    const channel = supabase
      .channel(`realtime_crm_conversation_${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mensagens',
          filter: `conversa_id=eq.${conversation.id}`
        },
        (payload) => {
          const newMsg = payload.new;
          processNewMessage(newMsg);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'mensagens',
          filter: `conversa_id=eq.${conversation.id}`
        },
        async (payload) => {
          const updatedMsg = payload.new;
          console.log('[CRMConversationModal] Mensagem atualizada:', updatedMsg);
          
          // Resolver mídia atualizada
          const mediaAudio = await resolvePlayableMedia(updatedMsg);
          const mediaImage = await resolveDisplayImage(updatedMsg);
          
          setMessages(prev => prev.map(m => {
            if (m.id === updatedMsg.id) {
              return {
                ...m,
                texto: updatedMsg.conteudo,
                audioUrl: mediaAudio.url || null,
                imageUrl: mediaImage.url || null,
                transcricao: updatedMsg.transcricao || m.transcricao
              };
            }
            return m;
          }));
        }
      )
      .subscribe((status) => {
        console.log('[CRMConversationModal] Realtime status:', status);
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [conversation?.id]);

  const loadConnectionInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('connections')
        .select('id, name, number, type')
        .eq('id', connectionId)
        .single();

      if (error) throw error;
      setConnectionInfo(data);
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao carregar conexão:', err);
    }
  };

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar conversa por contato_id + conexao_id
      const { data: conversaData, error: conversaError } = await supabase
        .from('conversas')
        .select('*')
        .eq('contato_id', contact.id)
        .eq('conexao_id', connectionId)
        .order('atualizado_em', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (conversaError) throw conversaError;

      if (!conversaData) {
        setError('Nenhuma conversa encontrada para este contato nesta conexão.');
        setLoading(false);
        return;
      }

      setConversation(conversaData);

      // Carregar mensagens
      await loadMessages(conversaData.id);

    } catch (err) {
      console.error('[CRMConversationModal] Erro ao carregar conversa:', err);
      setError('Erro ao carregar conversa: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const parseButtonPayload = (row) => {
    try {
      const meta = row?.metadados && typeof row.metadados === 'object' ? row.metadados : {};
      const parsed = meta?.parsed_button_payload && typeof meta.parsed_button_payload === 'object'
        ? meta.parsed_button_payload
        : null;
      if (!parsed) return null;

      const choices = Array.isArray(parsed.choices)
        ? parsed.choices.map((c) => String(c || '').trim()).filter(Boolean)
        : [];
      if (choices.length === 0) return null;

      return {
        text: String(parsed.text || row?.conteudo || '').trim() || null,
        choices,
        selectedText: String(parsed.selected_text || '').trim() || null
      };
    } catch (_) {
      return null;
    }
  };

  const loadMessages = async (conversationId) => {
    console.log('[CRMConversationModal] Carregando mensagens da conversa:', conversationId);
    try {
      const { data: messagesData, error } = await supabase
        .from('mensagens')
        .select('id, conteudo, tipo_conteudo, direcao, from_me, criado_em, url_midia, caminho_storage, transcricao, metadados')
        .eq('conversa_id', conversationId)
        .order('criado_em', { ascending: true })
        .limit(100);

      if (error) throw error;
      
      console.log('[CRMConversationModal] Mensagens do banco:', messagesData?.length, messagesData?.map(m => ({ id: m.id, tipo: m.tipo_conteudo, url: m.url_midia, storage: m.caminho_storage })));

      // Processar mensagens resolvendo URLs de mídia
      const processed = await Promise.all((messagesData || []).map(async msg => {
        const mediaAudio = await resolvePlayableMedia(msg);
        const mediaImage = await resolveDisplayImage(msg);
        const mediaDoc = await resolveDocument(msg);
        
        return {
          id: msg.id,
          texto: msg.conteudo,
          tipoConteudo: msg.tipo_conteudo,
          enviada: msg.from_me || msg.direcao === 'saida',
          hora: formatTime(msg.criado_em),
          criadoEm: msg.criado_em,
          audioUrl: mediaAudio.url || null,
          audioMime: mediaAudio.mime || null,
          imageUrl: mediaImage.url || null,
          documentUrl: mediaDoc.url || null,
          documentName: mediaDoc.name || null,
          documentSize: mediaDoc.size || null,
          transcricao: msg.transcricao,
          metadados: msg.metadados,
          buttonPayload: parseButtonPayload(msg)
        };
      }));
      
      console.log('[CRMConversationModal] Mensagens processadas:', processed.length, processed.map(m => ({ id: m.id, tipo: m.tipoConteudo, audio: m.audioUrl, image: m.imageUrl })));

      setMessages(processed);

    } catch (err) {
      console.error('[CRMConversationModal] Erro ao carregar mensagens:', err);
    }
  };

  const processNewMessage = async (newMsg) => {
    console.log('[CRMConversationModal] Nova mensagem realtime:', newMsg);
    console.log('[CRMConversationModal] url_midia:', newMsg.url_midia, 'caminho_storage:', newMsg.caminho_storage);

    // Resolver mídia
    const mediaAudio = await resolvePlayableMedia(newMsg);
    const mediaImage = await resolveDisplayImage(newMsg);
    const mediaDoc = await resolveDocument(newMsg);
    
    console.log('[CRMConversationModal] Mídia resolvida - audio:', mediaAudio.url, 'image:', mediaImage.url, 'doc:', mediaDoc.url);

    const processed = {
      id: newMsg.id,
      texto: newMsg.conteudo,
      tipoConteudo: newMsg.tipo_conteudo,
      enviada: newMsg.from_me || newMsg.direcao === 'saida',
      hora: formatTime(newMsg.criado_em),
      criadoEm: newMsg.criado_em,
      audioUrl: mediaAudio.url || null,
      audioMime: mediaAudio.mime || null,
      imageUrl: mediaImage.url || null,
      documentUrl: mediaDoc.url || null,
      documentName: mediaDoc.name || null,
      documentSize: mediaDoc.size || null,
      transcricao: newMsg.transcricao,
      metadados: newMsg.metadados,
      buttonPayload: parseButtonPayload(newMsg)
    };

    setMessages(prev => {
      const exists = prev.find(m => m.id === newMsg.id);
      if (exists) {
        // Se já existe, atualizar com a mídia se não tinha
        return prev.map(m => m.id === newMsg.id ? { ...m, ...processed } : m);
      }
      return [...prev, processed];
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    const texto = (newMessage || '').trim();
    if (!texto || !conversation?.id || !connectionId || sending) return;

    try {
      setSending(true);

      // Mensagem otimista
      const tempId = `local-${Date.now()}`;
      const optimisticMsg = {
        id: tempId,
        texto,
        tipoConteudo: 'text',
        enviada: true,
        hora: formatTime(new Date()),
        criadoEm: new Date().toISOString(),
        pending: true
      };

      setMessages(prev => [...prev, optimisticMsg]);
      setNewMessage('');

      // Focar no campo de texto para próxima mensagem
      if (inputMsgRef.current) inputMsgRef.current.focus();

      // Enviar via backend
      const response = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_TEXT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connection_id: connectionId,
          conversation_id: conversation.id,
          text: texto
        })
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[CRMConversationModal] Erro ao enviar:', response.status, errText);
        // Remover mensagem otimista
        setMessages(prev => prev.filter(m => m.id !== tempId));
        alert('Erro ao enviar mensagem');
        return;
      }

      // Remover mensagem otimista (o realtime vai adicionar a oficial)
      setTimeout(() => {
        setMessages(prev => prev.filter(m => m.id !== tempId));
      }, 2000);

    } catch (err) {
      console.error('[CRMConversationModal] Erro ao enviar:', err);
      alert('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  // ==========================
  // Upload de Imagem
  // ==========================
  const openImagePicker = () => {
    if (imageInputRef.current) imageInputRef.current.click();
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } catch (e) { reject(e); }
  });

  const onImageSelected = async (e) => {
    try {
      const files = e?.target?.files;
      if (!files || files.length === 0) return;
      const file = files[0];
      
      // Validar tipo
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        alert('Formato de imagem não suportado. Use JPG, PNG ou WEBP.');
        return;
      }
      
      // Validar tamanho (máx 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Imagem muito grande. Tamanho máximo: 10MB');
        return;
      }
      
      const dataUrl = await fileToDataUrl(file);
      setPendingImage({ dataUrl, mime: file.type || 'image/jpeg', name: file.name || 'imagem' });
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao selecionar imagem:', err);
      alert('Erro ao carregar imagem.');
    } finally {
      try { if (imageInputRef.current) imageInputRef.current.value = ''; } catch (_) {}
    }
  };

  const handleSendImage = async () => {
    if (!pendingImage || !conversation?.id || !connectionId || sending) return;
    
    try {
      setSending(true);
      
      const payload = {
        connection_id: connectionId,
        conversation_id: conversation.id,
        type: 'image',
        file: pendingImage.dataUrl,
        caption: newMessage?.trim() || undefined
      };
      
      const response = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[CRMConversationModal] Erro ao enviar imagem:', response.status, errText);
        alert('Erro ao enviar imagem');
        return;
      }
      
      setPendingImage(null);
      setNewMessage('');
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao enviar imagem:', err);
      alert('Erro ao enviar imagem');
    } finally {
      setSending(false);
    }
  };

  // ==========================
  // Upload de Documento
  // ==========================
  const openDocumentPicker = () => {
    if (documentInputRef.current) documentInputRef.current.click();
  };

  const onDocumentSelected = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validar tamanho (máx 100MB)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('Arquivo muito grande. Tamanho máximo: 100MB');
        return;
      }
      
      const dataUrl = await fileToDataUrl(file);
      setPendingDocument({ 
        dataUrl, 
        mime: file.type || 'application/octet-stream', 
        name: file.name || 'documento',
        size: file.size
      });
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao selecionar documento:', err);
      alert('Erro ao carregar documento.');
    } finally {
      try { if (documentInputRef.current) documentInputRef.current.value = ''; } catch (_) {}
    }
  };

  const handleSendDocument = async () => {
    if (!pendingDocument || !conversation?.id || !connectionId || sending) return;
    
    try {
      setSending(true);
      
      const payload = {
        connection_id: connectionId,
        conversation_id: conversation.id,
        type: 'document',
        file: pendingDocument.dataUrl,
        filename: pendingDocument.name
      };
      
      const response = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[CRMConversationModal] Erro ao enviar documento:', response.status, errText);
        alert('Erro ao enviar documento');
        return;
      }
      
      setPendingDocument(null);
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao enviar documento:', err);
      alert('Erro ao enviar documento');
    } finally {
      setSending(false);
    }
  };

  // ==========================
  // Gravação de Áudio
  // ==========================
  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    } catch (e) { reject(e); }
  });

  const handleMicClick = async () => {
    try {
      if (!window.isSecureContext) {
        alert('Este recurso requer contexto seguro (HTTPS).');
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Seu navegador não suporta captura de microfone.');
        return;
      }
      
      if (!isRecording) {
        // Iniciar gravação
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recordedChunksRef.current = [];
        
        const preferOgg = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg; codecs=opus');
        const mimeType = preferOgg ? 'audio/ogg; codecs=opus' : 'audio/webm';
        
        const mr = new MediaRecorder(stream, { mimeType });
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
        };
        mr.onstop = async () => {
          try {
            const blob = new Blob(recordedChunksRef.current, { type: mimeType });
            await sendRecordedAudio(blob);
          } catch (e) {
            console.error('[CRMConversationModal] Falha ao processar áudio gravado:', e);
          }
        };
        
        mediaRecorderRef.current = mr;
        mr.start();
        setIsRecording(true);
      } else {
        // Parar gravação
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== 'inactive') {
          mr.stop();
        }
        setIsRecording(false);
        // Parar tracks do stream
        try {
          const tracks = mr?.stream?.getTracks?.() || [];
          tracks.forEach(t => t.stop());
        } catch (_) {}
      }
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao acessar microfone:', err);
      alert('Não foi possível acessar o microfone. Verifique as permissões.');
    }
  };

  const sendRecordedAudio = async (blob) => {
    try {
      if (!blob || !conversation?.id || !connectionId) return;
      
      const dataUrl = await blobToDataUrl(blob);
      const payload = {
        connection_id: connectionId,
        conversation_id: conversation.id,
        type: 'ptt',
        file: dataUrl
      };
      
      const response = await fetch(API_ENDPOINTS.CONNECTIONS_SEND_MEDIA, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('[CRMConversationModal] Erro ao enviar áudio:', response.status, errText);
        alert('Erro ao enviar áudio');
      }
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao enviar áudio:', err);
      alert('Erro ao enviar áudio');
    }
  };

  // ==========================
  // Emoji Picker
  // ==========================
  const insertEmoji = (emoji) => {
    try {
      setNewMessage((prev) => `${prev || ''}${emoji}`);
      if (inputMsgRef.current) inputMsgRef.current.focus();
    } catch (_) {}
  };

  // Fechar emoji picker ao clicar fora
  useEffect(() => {
    const onDocClick = (e) => {
      if (showEmojiPicker && emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showEmojiPicker]);

  // ==========================
  // Lightbox
  // ==========================
  const openLightbox = (src, type = 'image') => {
    setLightboxSrc(src);
    setLightboxType(type);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxSrc('');
    setLightboxType('');
  };

  // ==========================
  // Formatação
  // ==========================
  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const toTimestampSafe = (value) => {
    if (!value) return null;
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : null;
  };

  // ==========================
  // Resolução de Mídia do Storage
  // ==========================
  
  // Cache para URLs assinadas
  const signedUrlCache = useRef(new Map());

  // Detecta e extrai referência de Storage (bucket + path) de uma URL/caminho
  const parseStorageRef = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const raw = String(url).trim();
      
      // Rejeitar padrões externos
      if (/^(https?|ftp)\//i.test(raw)) return null;
      if (/whatsapp\.net|fbcdn\.net|cdn\.|googleusercontent\.com|akamaihd\.net/i.test(raw)) return null;
      
      // URL padrão do Supabase Storage
      const m = raw.match(/\/storage\/v1\/object\/(public\/)?([^/]+)\/(.+)$/);
      if (m) {
        return { bucket: m[2], path: m[3] };
      }
      
      // Referência curta "bucket:path"
      if (raw.includes(':')) {
        const [bucket, ...rest] = raw.split(':');
        const path = rest.join(':').replace(/^\//, '');
        if (bucket && path) return { bucket, path };
      }
      
      // Caminho simples "bucket/dir/file"
      if (!raw.startsWith('http') && raw.includes('/')) {
        const [bucket, ...rest] = raw.split('/');
        const path = rest.join('/');
        if (bucket && path) return { bucket, path };
      }
      
      return null;
    } catch (_) {
      return null;
    }
  };

  // Obtém URL assinada do Storage via backend
  const getSignedUrl = async (bucket, path, expiresIn = 3600) => {
    const key = `${bucket}:${path}`;
    const now = Date.now();
    const cached = signedUrlCache.current.get(key);
    
    if (cached && cached.expiresAt > now + 5000) {
      return cached.url;
    }
    
    try {
      const caminho_storage = `${bucket}:${path}`;
      const response = await fetch(API_ENDPOINTS.MEDIA_SIGNED_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          caminho_storage,
          empresa_id: companyId 
        })
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      const url = data?.signedUrl || null;
      
      if (url) {
        signedUrlCache.current.set(key, { url, expiresAt: now + (data.expiresIn || expiresIn) * 1000 });
      }
      
      return url;
    } catch (err) {
      console.error('[CRMConversationModal] Erro ao obter URL assinada:', err);
      return null;
    }
  };

  // Tenta parsear JSON do campo conteudo
  const parseConteudoJson = (texto) => {
    try {
      if (!texto || typeof texto !== 'string') return null;
      const t = texto.trim();
      if (!(t.startsWith('{') && t.endsWith('}'))) return null;
      const obj = JSON.parse(t);
      return obj && typeof obj === 'object' ? obj : null;
    } catch (_) {
      return null;
    }
  };

  // Resolve URL reproduzível para áudios
  const resolvePlayableMedia = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      const jsonMime = String(json?.mimetype || '').toLowerCase();
      
      const looksAudio = type.startsWith('audio') || jsonMime.startsWith('audio') || type === 'ptt' || type === 'myaudio';
      if (!looksAudio) return { url: null, mime: null };
      
      const effectiveMime = type.startsWith('audio') ? type : (json?.mimetype || 'audio/mpeg');
      
      // Verificar se é URL criptografada (.enc)
      const isEncUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        return /\.enc(\?|$)/.test(u.toLowerCase());
      };
      
      // 1) caminho_storage (assinar)
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) return { url: signed, mime: effectiveMime };
        }
      }
      
      // 2) url_midia direta (recusar .enc)
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia)) && !isEncUrl(row.url_midia)) {
        return { url: row.url_midia, mime: effectiveMime };
      }
      
      // 3) Data URL no conteúdo
      if (typeof row?.conteudo === 'string' && /^data:audio\//i.test(row.conteudo.trim())) {
        return { url: row.conteudo.trim(), mime: effectiveMime };
      }
      
      return { url: null, mime: null };
    } catch (_) {
      return { url: null, mime: null };
    }
  };

  // Resolve URL exibível para imagens
  const resolveDisplayImage = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      const jsonMime = String(json?.mimetype || '').toLowerCase();
      
      const looksImage = type.startsWith('image') || jsonMime.startsWith('image');
      if (!looksImage) return { url: null, mime: null };
      
      const effectiveMime = type.startsWith('image') ? type : (json?.mimetype || 'image/jpeg');
      
      // Verificar se é URL criptografada (.enc)
      const isEncUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        return /\.enc(\?|$)/.test(u.toLowerCase());
      };
      
      // 1) caminho_storage (assinar)
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) return { url: signed, mime: effectiveMime };
        }
      }
      
      // 2) url_midia direta (recusar .enc)
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia)) && !isEncUrl(row.url_midia)) {
        return { url: row.url_midia, mime: effectiveMime };
      }
      
      // 3) Data URL no conteúdo
      if (typeof row?.conteudo === 'string' && /^data:image\//i.test(row.conteudo.trim())) {
        return { url: row.conteudo.trim(), mime: effectiveMime };
      }
      
      // 4) Preview JPEGThumbnail do JSON
      const thumb = json?.JPEGThumbnail ? `data:image/jpeg;base64,${json.JPEGThumbnail}` : null;
      if (thumb) return { url: thumb, mime: 'image/jpeg' };
      
      return { url: null, mime: null };
    } catch (_) {
      return { url: null, mime: null };
    }
  };

  const isAudioType = (tipo) => {
    if (!tipo) return false;
    const t = tipo.toLowerCase();
    return t === 'audio' || t.startsWith('audio/') || t === 'ptt' || t === 'myaudio';
  };

  const isImageType = (tipo) => {
    if (!tipo) return false;
    const t = tipo.toLowerCase();
    return t === 'image' || t.startsWith('image/');
  };

  const isDocumentType = (tipo) => {
    if (!tipo) return false;
    const t = tipo.toLowerCase();
    return t === 'document' || t === 'arquivo' || t.startsWith('application/') || 
           t.includes('pdf') || t.includes('doc') || t.includes('xls') || t.includes('zip');
  };

  const resolveDocument = async (row) => {
    try {
      const type = String(row?.tipo_conteudo || '').toLowerCase();
      const json = parseConteudoJson(row?.conteudo);
      
      const looksDocument = isDocumentType(type) || (json?.filename && !isImageType(type) && !isAudioType(type));
      if (!looksDocument) return { url: null, name: null, size: null };
      
      const filename = json?.filename || row?.metadados?.filename || 'documento';
      const filesize = json?.fileSize || row?.metadados?.fileSize || null;
      
      // 1) caminho_storage (assinar)
      if (row?.caminho_storage) {
        const ref = parseStorageRef(row.caminho_storage);
        if (ref) {
          const signed = await getSignedUrl(ref.bucket, ref.path, 3600);
          if (signed) return { url: signed, name: filename, size: filesize };
        }
      }
      
      // 2) url_midia direta
      if (row?.url_midia && /^https?:\/\//i.test(String(row.url_midia))) {
        return { url: row.url_midia, name: filename, size: filesize };
      }
      
      return { url: null, name: null, size: null };
    } catch (_) {
      return { url: null, name: null, size: null };
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const withoutCountry = cleaned.startsWith('55') ? cleaned.slice(2) : cleaned;
    if (withoutCountry.length === 11) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 7)}-${withoutCountry.slice(7)}`;
    }
    if (withoutCountry.length === 10) {
      return `(${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 6)}-${withoutCountry.slice(6)}`;
    }
    return phone;
  };

  const getContactName = () => {
    return contact?.nome || card?.title || 'Contato';
  };

  const getContactNumber = () => {
    return formatPhoneNumber(contact?.numero || card?.metadata?.phone || '');
  };

  const getConnectionDisplay = () => {
    if (!connectionInfo) return 'Conexão';
    const name = connectionInfo.name || 'Conexão';
    const number = formatPhoneNumber(connectionInfo.number || '');
    return number ? `${name} (${number})` : name;
  };

  // Agrupar mensagens por dia (mais recentes em cima)
  const groupMessagesByDay = () => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    // Ordenar mensagens por data ascendente (mais antigas primeiro, como no WhatsApp)
    const sortedMessages = [...messages]
      .map((msg, idx) => ({ msg, idx }))
      .sort((a, b) => {
        const dateA = toTimestampSafe(a.msg.criadoEm);
        const dateB = toTimestampSafe(b.msg.criadoEm);

        // Preserve deterministic order when timestamp is missing/invalid
        if (dateA === null && dateB === null) return a.idx - b.idx;
        if (dateA === null) return 1;
        if (dateB === null) return -1;
        if (dateA !== dateB) return dateA - dateB;

        // Tie-breaker avoids random flickering when same timestamp
        const idA = String(a.msg.id || '');
        const idB = String(b.msg.id || '');
        return idA.localeCompare(idB);
      })
      .map(({ msg }) => msg);

    sortedMessages.forEach(msg => {
      const msgDate = formatDate(msg.criadoEm);
      if (msgDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = msgDate;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  };

  if (!isOpen) return null;

  return (
    <div className="crm-conversation-modal-overlay" onClick={onClose}>
      <div className="crm-conversation-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="crm-conversation-header">
          <div className="crm-conversation-contact-info">
            <div className="crm-conversation-avatar">
              {contact?.image_url ? (
                <img src={contact.image_url} alt={getContactName()} />
              ) : (
                <AppIcon name="person" />
              )}
            </div>
            <div className="crm-conversation-details">
              <h2>{getContactName()}</h2>
              <p className="crm-conversation-contact-number">{getContactNumber()}</p>
              <p className="crm-conversation-connection">
                <AppIcon name="link" />
                via {getConnectionDisplay()}
              </p>
            </div>
          </div>
          <button className="crm-conversation-close" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="crm-conversation-messages" ref={messagesContainerRef}>
          {loading ? (
            <div className="crm-conversation-loading">
              <AppIcon name="progress_activity" className="spinning" />
              <p>Carregando conversa...</p>
            </div>
          ) : error ? (
            <div className="crm-conversation-error">
              <AppIcon name="error" />
              <p>{error}</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="crm-conversation-empty">
              <AppIcon name="chat_bubble_outline" />
              <p>Nenhuma mensagem encontrada</p>
            </div>
          ) : (
            groupMessagesByDay().map(group => (
              <div key={group.date} className="crm-conversation-day-group">
                <div className="crm-conversation-day-separator">
                  <span>{group.date}</span>
                </div>
                {group.messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`crm-conversation-message ${msg.enviada ? 'sent' : 'received'}`}
                  >
                    <div className="crm-conversation-message-content">
                      {/* Áudio com player */}
                      {msg.audioUrl && (
                        <div className="crm-conversation-audio">
                          <audio controls preload="metadata" style={{ width: '100%', maxWidth: '280px' }}>
                            <source src={msg.audioUrl} type={msg.audioMime || 'audio/mpeg'} />
                            Seu navegador não suporta áudio.
                          </audio>
                          {msg.transcricao && (
                            <p className="crm-conversation-transcription">{msg.transcricao}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Imagem */}
                      {msg.imageUrl && (
                        <div className="crm-conversation-image">
                          <img 
                            src={msg.imageUrl} 
                            alt="Imagem" 
                            onClick={() => openLightbox(msg.imageUrl, 'image')}
                          />
                          {msg.texto && msg.texto !== '[imagem]' && (
                            <p>{msg.texto}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Documento */}
                      {msg.documentUrl && (
                        <div className="crm-conversation-document">
                          <a href={msg.documentUrl} target="_blank" rel="noopener noreferrer" className="crm-conversation-document-link">
                            <AppIcon name="description" />
                            <div className="crm-conversation-document-info">
                              <span className="crm-conversation-document-name">{msg.documentName || 'Documento'}</span>
                              {msg.documentSize && (
                                <span className="crm-conversation-document-size">
                                  {msg.documentSize > 1024 * 1024 
                                    ? `${(msg.documentSize / (1024 * 1024)).toFixed(1)} MB`
                                    : `${(msg.documentSize / 1024).toFixed(0)} KB`}
                                </span>
                              )}
                            </div>
                            <AppIcon name="download" />
                          </a>
                        </div>
                      )}
                      
                      {/* Texto */}
                      {!msg.audioUrl && !msg.imageUrl && !msg.documentUrl && msg.texto && (
                        <p>{msg.texto}</p>
                      )}

                      {/* Mensagem com botões */}
                      {!msg.audioUrl && !msg.imageUrl && !msg.documentUrl && msg.buttonPayload?.choices?.length > 0 && (
                        <div className="crm-conversation-buttons-block">
                          {msg.buttonPayload.text && (
                            <p>{msg.buttonPayload.text}</p>
                          )}
                          <div className="crm-conversation-buttons-list">
                            {msg.buttonPayload.choices.map((choice, idx) => (
                              <div key={`${msg.id}-choice-${idx}`} className="crm-conversation-button-chip">
                                {choice}
                              </div>
                            ))}
                          </div>
                          {msg.buttonPayload.selectedText && (
                            <p className="crm-conversation-button-selected">Selecionado: {msg.buttonPayload.selectedText}</p>
                          )}
                        </div>
                      )}
                      
                      <span className="crm-conversation-message-time">
                        {msg.hora}
                        {msg.pending && <span className="pending-indicator">...</span>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Input Area */}
        {!error && conversation && (
          <>
            {/* Pré-visualização de Imagem Pendente */}
            {pendingImage && (
              <div className="crm-conversation-pending-media">
                <div className="crm-conversation-pending-image">
                  <img src={pendingImage.dataUrl} alt="Pré-visualização" />
                  <button 
                    className="crm-conversation-pending-remove"
                    onClick={() => setPendingImage(null)}
                    type="button"
                  >
                    <AppIcon name="close" />
                  </button>
                </div>
                <div className="crm-conversation-pending-actions">
                  <input
                    type="text"
                    placeholder="Adicionar legenda (opcional)..."
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    className="crm-conversation-caption-input"
                  />
                  <button
                    type="button"
                    className="crm-conversation-send-media-btn"
                    onClick={handleSendImage}
                    disabled={sending}
                  >
                    <AppIcon name={sending ? 'progress_activity' : 'send'} />
                  </button>
                </div>
              </div>
            )}

            {/* Pré-visualização de Documento Pendente */}
            {pendingDocument && (
              <div className="crm-conversation-pending-media">
                <div className="crm-conversation-pending-document">
                  <AppIcon name="description" />
                  <div className="crm-conversation-pending-document-info">
                    <span className="crm-conversation-pending-document-name">{pendingDocument.name}</span>
                    <span className="crm-conversation-pending-document-size">
                      {pendingDocument.size > 1024 * 1024 
                        ? `${(pendingDocument.size / (1024 * 1024)).toFixed(1)} MB`
                        : `${(pendingDocument.size / 1024).toFixed(0)} KB`}
                    </span>
                  </div>
                  <button 
                    className="crm-conversation-pending-remove"
                    onClick={() => setPendingDocument(null)}
                    type="button"
                  >
                    <AppIcon name="close" />
                  </button>
                </div>
                <div className="crm-conversation-pending-actions">
                  <button
                    type="button"
                    className="crm-conversation-send-media-btn"
                    onClick={handleSendDocument}
                    disabled={sending}
                  >
                    <AppIcon name={sending ? 'progress_activity' : 'send'} />
                    Enviar documento
                  </button>
                </div>
              </div>
            )}

            {/* Input normal (quando não há mídia pendente) */}
            {!pendingImage && !pendingDocument && (
              <form className="crm-conversation-input-area" onSubmit={handleSendMessage}>
                {/* Botões de mídia */}
                <div className="crm-conversation-media-buttons">
                  {/* Emoji Picker */}
                  <div className="crm-conversation-emoji-wrapper" ref={emojiPickerRef}>
                    <button
                      type="button"
                      className="crm-conversation-media-btn"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    >
                      <AppIcon name="mood" />
                    </button>
                    {showEmojiPicker && (
                      <div className="crm-conversation-emoji-picker">
                        {EMOJIS.map((emoji, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="crm-conversation-emoji-btn"
                            onClick={() => insertEmoji(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Upload de Imagem */}
                  <button
                    type="button"
                    className="crm-conversation-media-btn"
                    onClick={openImagePicker}
                    title="Enviar imagem"
                  >
                    <AppIcon name="image" />
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={onImageSelected}
                    style={{ display: 'none' }}
                  />

                  {/* Upload de Documento */}
                  <button
                    type="button"
                    className="crm-conversation-media-btn"
                    onClick={openDocumentPicker}
                    title="Enviar documento"
                  >
                    <AppIcon name="attach_file" />
                  </button>
                  <input
                    ref={documentInputRef}
                    type="file"
                    onChange={onDocumentSelected}
                    style={{ display: 'none' }}
                  />

                  {/* Gravação de Áudio */}
                  <button
                    type="button"
                    className={`crm-conversation-media-btn ${isRecording ? 'recording' : ''}`}
                    onClick={handleMicClick}
                    title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
                  >
                    <AppIcon name={isRecording ? 'stop' : 'mic'} />
                  </button>
                </div>

                {/* Campo de texto */}
                <input
                  ref={inputMsgRef}
                  type="text"
                  placeholder={isRecording ? 'Gravando áudio...' : 'Digite uma mensagem...'}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  disabled={sending || isRecording}
                />

                {/* Botão enviar */}
                <button
                  type="submit"
                  className="crm-conversation-send-btn"
                  disabled={!newMessage.trim() || sending || isRecording}
                >
                  <AppIcon name={sending ? 'progress_activity' : 'send'} />
                </button>
              </form>
            )}
          </>
        )}

        {/* Lightbox para visualização de mídia */}
        {isLightboxOpen && (
          <div className="crm-conversation-lightbox" onClick={closeLightbox}>
            <button className="crm-conversation-lightbox-close" onClick={closeLightbox}>
              <AppIcon name="close" />
            </button>
            {lightboxType === 'image' && (
              <img src={lightboxSrc} alt="Visualização" onClick={e => e.stopPropagation()} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
